import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Printer } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../ui/Toast';
import { PrintDocumentDialog } from '../PrintDocumentDialog';
import { enumLabel } from '../../lib/enumLabel';
import { formatDate, formatEUR } from '../../lib/format';

interface DocRow {
  id: string;
  docType: string;
  number: string;
  date: string;
  status: string | null;
  totalTTC: number;
  customerId: string | null;
  customerCode: string | null;
  customerName: string | null;
}

/** What the print dialog can render (routes/documents.ts PRINTABLE_TYPES). */
const PRINTABLE = new Set(['INVOICE', 'QUOTE', 'CREDIT_NOTE', 'DELIVERY_NOTE', 'ORDER']);

/**
 * Every document of the order in one place (order-sheet prototype, "Documents given to the
 * customer"): the delivery notes with their two copies, the invoices (deposit and final), the
 * credit notes, the quote it came from, the pickup slip. Each printable one opens the print
 * dialog — print, PDF, e-mail, with its print and mail history. A reprint of an invoice or a
 * credit note carries DUPLICATA, decided by the server (services/pdf/duplicate.ts).
 *
 * The list is GET /documents?orderId= — the same per-family permissions as the document
 * register, so a role that may not read invoices does not get them here either.
 */
export function OrderDocuments({ orderId, refreshKey }: { orderId: string; refreshKey: number }) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [rows, setRows] = useState<DocRow[] | null>(null);
  const [printing, setPrinting] = useState<DocRow | null>(null);

  useEffect(() => {
    let alive = true;
    apiClient
      .get<{ content: DocRow[] }>('/documents', {
        params: { orderId, size: 100, sort: 'date,asc' },
      })
      .then((r) => {
        if (alive) setRows(r.data.content ?? []);
      })
      .catch(() => {
        if (alive) setRows([]);
      });
    return () => {
      alive = false;
    };
  }, [orderId, refreshKey]);

  // A delivery note has two copies: the driver's (no prices, signed on receipt) and the
  // customer's (priced). Fetched through apiClient — it owns the base URL and the token.
  const openBl = async (row: DocRow, variant: 'driver' | 'client', download: boolean) => {
    try {
      const path =
        variant === 'client'
          ? `/delivery-notes/${row.id}/client-pdf`
          : `/delivery-notes/${row.id}/pdf`;
      const res = await apiClient.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${variant === 'client' ? 'bon-livraison-client' : 'bon-livraison'}-${row.number}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank', 'noopener');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showApiError(err);
    }
  };

  if (!rows || rows.length === 0) return null;
  const customer = rows.find((r) => r.customerCode || r.customerName);

  return (
    <section className="border rounded p-4 bg-card">
      <h2 className="font-semibold mb-2">{t('orders.documents.title')}</h2>
      <ul className="divide-y text-sm">
        {rows.map((r) => (
          <li key={`${r.docType}:${r.id}`} className="flex flex-wrap items-center gap-3 py-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="w-32 text-xs text-muted-foreground">
              {t(`orders.documents.type.${r.docType}`, { defaultValue: r.docType })}
            </span>
            <span className="font-mono">{r.number}</span>
            <span className="text-xs text-muted-foreground">{formatDate(r.date)}</span>
            {r.status && (
              <span className="text-xs text-muted-foreground">
                {enumLabel('docStatus', r.status)}
              </span>
            )}
            {r.docType !== 'DELIVERY_NOTE' && r.docType !== 'PICKUP_SLIP' && (
              <span className="tabular-nums text-xs">{formatEUR(Number(r.totalTTC))}</span>
            )}
            <div className="ml-auto flex flex-wrap gap-3">
              {r.docType === 'DELIVERY_NOTE' && (
                <>
                  <button
                    onClick={() => void openBl(r, 'driver', true)}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Download className="h-3.5 w-3.5" /> {t('orders.detail.bl.driverPdf')}
                  </button>
                  <button
                    onClick={() => void openBl(r, 'client', true)}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                    title={t('orders.detail.bl.clientPdfTitle')}
                  >
                    <Download className="h-3.5 w-3.5" /> {t('orders.detail.bl.clientPdf')}
                  </button>
                </>
              )}
              {PRINTABLE.has(r.docType) && (
                <button
                  onClick={() => setPrinting(r)}
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  <Printer className="h-3.5 w-3.5" /> {t('orders.documents.printEmail')}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{t('orders.documents.frozen')}</span>
        {customer && (
          <Link
            to={`/documents?customer=${encodeURIComponent(customer.customerCode || customer.customerName || '')}`}
            className="text-primary hover:underline"
          >
            {t('orders.documents.allOfCustomer', { name: customer.customerName ?? '' })}
          </Link>
        )}
      </div>
      {printing && (
        <PrintDocumentDialog
          docType={
            printing.docType as 'INVOICE' | 'QUOTE' | 'CREDIT_NOTE' | 'DELIVERY_NOTE' | 'ORDER'
          }
          documentId={printing.id}
          documentNumber={printing.number}
          customerId={printing.customerId}
          orderId={orderId}
          onClose={() => setPrinting(null)}
        />
      )}
    </section>
  );
}
